import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  CreditGuarantorDto,
  CreditGuarantorsResponseDto,
  DisbursementGuarantorDto,
  GuarantorLimitUtilizedDto,
  ProposedDisbursementNoteDto,
} from '../models/proposed-disbursement.model';

/** Figures the "Cover note" sheet needs beyond what the note DTO carries. */
export interface CoverNoteExportData {
  note: ProposedDisbursementNoteDto;
  amountRecommended: number | null;
  amountInWordsText: string;
  exposurePostDisbursement: number;
  comments: string;
}

/** One row of the covenant compliance table on the "Disbursement note" (Annexure I) sheet. */
export interface CovenantExportRow {
  srNo: number;
  description: string;
  complianceStatus: string;
  remarks: string;
}

/** One indicator row of the "Latest Financials" table — values line up with `financialPeriods`. */
export interface FinancialExportRow {
  indicator: string;
  values: Array<string | number>;
}

/** Everything the "Disbursement note" (Annexure I) sheet needs on top of the Cover Note figures. */
export interface DisbursementNoteWorkbookData extends CoverNoteExportData {
  covenantRows: CovenantExportRow[];
  /** CAD tab questions with their answers — the "CAD" (Annexure II) checklist. */
  cadRows: CovenantExportRow[];
  financialPeriods: string[];
  financialRows: FinancialExportRow[];
  /** Guarantor limits utilized / remaining — fills the "Cumulative" limit block. */
  guarantorLimits: GuarantorLimitUtilizedDto[];
  /** Guarantee figures of the proposal — fills the "Guarantee Calculations" table. */
  creditGuarantors: CreditGuarantorsResponseDto | null;
  /** Saved guarantor shares of the disbursement — fills the "By the Borrower" limit block. */
  guarantorShares: DisbursementGuarantorDto[];
  /**
   * `get-clnt-otstndng/{clntSeq}/{fcltyTypKey}` per facility type (17 Conventional,
   * 18 Islamic) — its `totalAmount` heads that type in Total Outstanding Exposure.
   */
  clientOutstanding?: { fcltyTypKey: number; response: unknown }[];
}

// ─────────────────────────────── Style primitives ───────────────────────────────

const THIN: ExcelJS.Border = { style: 'thin', color: { argb: 'FF000000' } };
const MEDIUM: ExcelJS.Border = { style: 'medium', color: { argb: 'FF000000' } };
const DOUBLE: ExcelJS.Border = { style: 'double', color: { argb: 'FF000000' } };

/** `_(* #,##0_)` — the template's accounting format. */
const ACCOUNTING_FMT = '_(* #,##0_);_(* (#,##0);_(* "-"??_);_(@_)';
const ACCOUNTING_2DP_FMT = '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)';
/** `#,##0 ` — the template's plain integer format (breakdown lines, CAD side panel). */
const NUMBER_FMT = '#,##0_ ;\\-#,##0\\ ';
/** `_-* #,##0_-` — the template's format for the Guarantee Calculations table. */
const GUARANTEE_FMT = '_-* #,##0_-;\\-* #,##0_-;_-* "-"??_-;_-@_-';
const DATE_FMT = 'd-mmm-yy';

/** Template fills: "White, Background 1, Darker 15%" and "Blue-Gray, Text 2". */
const GREY_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
const DARK_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF44546A' } };

const DASH = '-';

/** Annexure I's "Latest Financials" table has exactly 3 value columns (G, H, I): Latest Monthly + two years. */
const FINANCIAL_PERIOD_COLUMNS = ['G', 'H', 'I'];

/** Breakdown lines under every Conventional / Islamic block of Annexure I. */
const FUNDING_SOURCES = ['PMIC', 'DFC', 'NCGCL', 'BII'];

/** Repayment term / facility type codes (ref code group 13). */
const CONVENTIONAL_TERM = 17;
const ISLAMIC_TERM = 18;

type CellValue = string | number | Date | null | ExcelJS.CellFormulaValue;

interface CellStyleOptions {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  size?: number;
  horizontal?: ExcelJS.Alignment['horizontal'];
  vertical?: ExcelJS.Alignment['vertical'];
  wrap?: boolean;
  numFmt?: string;
  fill?: ExcelJS.Fill;
  /** `true` for a full thin box; or an explicit subset of sides. */
  border?: boolean | Partial<ExcelJS.Borders>;
}

type Sides = Partial<Record<'top' | 'left' | 'bottom' | 'right', ExcelJS.Border>>;

const formula = (expression: string, result?: number | string): ExcelJS.CellFormulaValue =>
  ({ formula: expression, result } as ExcelJS.CellFormulaValue);

/**
 * Thin wrapper around a worksheet so every sheet builder shares one set of
 * cell/merge/border helpers. Borders are always *added* to a cell (never
 * replaced) and are routed to a merge's master cell — ExcelJS shares one
 * style object across a merge, so the master's sides draw the whole block.
 */
class SheetWriter {
  constructor(
    readonly sheet: ExcelJS.Worksheet,
    private readonly defaultSize: number,
  ) {}

  set(range: string, value: CellValue, options: CellStyleOptions = {}): void {
    if (range.includes(':')) {
      this.sheet.mergeCells(range);
    }

    const cell = this.sheet.getCell(range.split(':')[0]);

    if (value !== null) {
      cell.value = value;
    }

    cell.font = {
      name: 'Calibri',
      family: 2,
      scheme: 'minor',
      bold: options.bold ?? false,
      italic: options.italic ?? false,
      underline: options.underline ?? false,
      size: options.size ?? this.defaultSize,
    };
    cell.alignment = {
      horizontal: options.horizontal,
      vertical: options.vertical,
      wrapText: options.wrap ?? false,
    };

    if (options.numFmt) {
      cell.numFmt = options.numFmt;
    }

    if (options.fill) {
      cell.fill = options.fill;
    }

    if (options.border === true) {
      this.border(cell.address, { top: THIN, left: THIN, bottom: THIN, right: THIN });
    } else if (options.border) {
      this.border(cell.address, options.border as Sides);
    }
  }

  /** Adds border sides to a cell (or the merge it belongs to) without clobbering existing sides. */
  border(address: string, sides: Sides): void {
    const target = this.anchor(address);

    target.border = { ...target.border, ...sides };
  }

  /** Adds the same sides to every cell of a single-row run, e.g. `line(3, 'A', 'J', { top: MEDIUM })`. */
  line(row: number, fromCol: string, toCol: string, sides: Sides): void {
    for (let col = colNumber(fromCol); col <= colNumber(toCol); col++) {
      this.border(`${colLetter(col)}${row}`, sides);
    }
  }

  /** Draws the outer rectangle of a range with the given edge style. */
  outline(range: string, edge: ExcelJS.Border = THIN): void {
    const [start, end] = range.split(':');
    const { col: c1, row: r1 } = splitAddress(start);
    const { col: c2, row: r2 } = splitAddress(end);

    for (let col = c1; col <= c2; col++) {
      this.border(`${colLetter(col)}${r1}`, { top: edge });
      this.border(`${colLetter(col)}${r2}`, { bottom: edge });
    }

    for (let row = r1; row <= r2; row++) {
      this.border(`${colLetter(c1)}${row}`, { left: edge });
      this.border(`${colLetter(c2)}${row}`, { right: edge });
    }
  }

  /** Left edge on `leftCol` and right edge on `rightCol` for every row in the span. */
  sides(fromRow: number, toRow: number, leftCol: string, rightCol: string, edge: ExcelJS.Border = MEDIUM): void {
    for (let row = fromRow; row <= toRow; row++) {
      this.border(`${leftCol}${row}`, { left: edge });
      this.border(`${rightCol}${row}`, { right: edge });
    }
  }

  heights(heights: Record<number, number>): void {
    Object.entries(heights).forEach(([row, height]) => {
      this.sheet.getRow(Number(row)).height = height;
    });
  }

  private anchor(address: string): ExcelJS.Cell {
    const cell = this.sheet.getCell(address);

    return cell.isMerged ? cell.master : cell;
  }
}

function colNumber(letters: string): number {
  return letters.split('').reduce((total, char) => total * 26 + (char.charCodeAt(0) - 64), 0);
}

function colLetter(col: number): string {
  let letters = '';

  while (col > 0) {
    const rem = (col - 1) % 26;

    letters = String.fromCharCode(65 + rem) + letters;
    col = Math.floor((col - 1) / 26);
  }

  return letters;
}

function splitAddress(address: string): { col: number; row: number } {
  const match = /^([A-Z]+)(\d+)$/.exec(address);

  if (!match) {
    throw new Error(`Invalid cell address: ${address}`);
  }

  return { col: colNumber(match[1]), row: Number(match[2]) };
}

/** Rough wrapped-row height for a 12pt cell whose merged width fits ~`charsPerLine` characters. */
function wrapHeight(texts: string[], charsPerLine: number, minHeight = 15.6): number {
  const lines = Math.max(
    1,
    ...texts.map((text) =>
      (text || '')
        .split('\n')
        .reduce((sum, part) => sum + Math.max(1, Math.ceil(part.length / charsPerLine)), 0),
    ),
  );

  return lines === 1 ? minHeight : lines * 16.2;
}

/**
 * The screen hands financial figures over already formatted ("1,103",
 * "0.07", "-"). Numbers go back into the sheet as numbers so the template's
 * accounting format applies; anything else is written as text.
 */
function financialCell(value: string | number | undefined): { value: CellValue; numFmt?: string } {
  if (value === undefined || value === null || value === '') {
    return { value: DASH };
  }

  const numeric = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());

  if (String(value).trim() === DASH || Number.isNaN(numeric)) {
    return { value: String(value) };
  }

  return { value: numeric, numFmt: Number.isInteger(numeric) ? ACCOUNTING_FMT : ACCOUNTING_2DP_FMT };
}

/**
 * Builds and downloads the Excel workbook behind the Disbursement Note
 * screen's "Download Report" button. It replicates the bank's
 * "Disbursement Note Format - Jul'26" template sheet by sheet — Cover note,
 * Disbursement note (Annexure I) and CAD (Annexure II) — with the live
 * figures from the screen. Cross-sheet figures are kept as the template's
 * own formulas (with cached results), so edits in Excel still flow through.
 */
@Injectable({ providedIn: 'root' })
export class DisbursementNoteExportService {
  download(data: DisbursementNoteWorkbookData): void {
    const workbook = new ExcelJS.Workbook();

    // Formula cells carry cached results, but Excel recomputes on open so the
    // workbook behaves exactly like the template once the user edits a figure.
    workbook.calcProperties.fullCalcOnLoad = true;

    this.buildCoverNoteSheet(workbook, data);

    const annexure = this.buildDisbursementNoteSheet(workbook, data);

    this.buildCADSheet(workbook, data, annexure);

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const borrower = (data.note.brwrNm || 'disbursement').trim().replace(/\s+/g, '_');

      saveAs(blob, `Disbursement_Note_${borrower}.xlsx`);
    });
  }

  // ═══════════════════════════════ COVER NOTE ═══════════════════════════════

  /** Replicates the template's "Cover note" sheet (A1:J31, portrait). */
  private buildCoverNoteSheet(workbook: ExcelJS.Workbook, data: CoverNoteExportData): void {
    const { note } = data;
    const sheet = workbook.addWorksheet('Cover note', {
      properties: { defaultRowHeight: 14.4 },
      views: [{ showGridLines: false }],
      pageSetup: {
        orientation: 'portrait',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1,
        printArea: 'A1:J31',
        margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
      },
    });
    const w = new SheetWriter(sheet, 11);

    sheet.columns = [
      {},
      { width: 27.77734375 },
      { width: 19.21875 },
      { width: 19.5546875 },
      { width: 15.5546875 },
      { width: 13.5546875 },
      { width: 16 },
      { width: 17.21875 },
      { width: 12.77734375 },
      { width: 16.77734375 },
      { width: 9.5546875 },
      { width: 13.5546875 },
    ];

    w.heights({ 1: 18, 2: 18.6, 4: 15.6, 6: 15.6, 8: 15.6, 9: 15.6, 10: 15.6, 11: 15.6, 12: 60.45, 31: 15 });

    const amountRecommended = Number(data.amountRecommended ?? 0);

    // ── Title ──
    w.set('A1:J1', 'PAKISTAN MICROFINANCE INVESTMENT COMPANY LIMITED', {
      bold: true,
      size: 14,
      horizontal: 'center',
      border: { top: MEDIUM },
    });
    w.set('A2:J2', 'DISBURSEMENT NOTE - Financing', {
      bold: true,
      size: 14,
      horizontal: 'center',
      border: { bottom: MEDIUM },
    });
    w.line(3, 'A', 'J', { top: MEDIUM });

    // ── Borrower / date ──
    w.set('A4', 'Borrower Name', { bold: true, border: { top: THIN, bottom: THIN } });
    w.border('B4', { top: THIN, bottom: THIN, right: THIN });
    w.set('C4:H4', note.brwrNm || DASH, { bold: true, size: 12, horizontal: 'center', border: true });
    w.set('I4', 'Date:', { bold: true, horizontal: 'center' });
    w.set('J4', new Date(), { horizontal: 'center', vertical: 'middle', numFmt: DATE_FMT, border: true });

    // ── Facility / installment / tranche ──
    w.set('A6:B6', 'Facility Name:', { bold: true, vertical: 'middle', border: true });
    w.set('C6:E6', note.fcltyNm || DASH, { bold: true, size: 12, horizontal: 'center', border: true });
    w.set('F6:G6', 'Installment No: ', {
      bold: true,
      horizontal: 'center',
      vertical: 'middle',
      border: { left: THIN, right: THIN },
    });
    w.set('H6', note.installmentNo ?? DASH, {
      size: 12,
      horizontal: 'center',
      vertical: 'middle',
      numFmt: ACCOUNTING_FMT,
      border: true,
    });
    w.set('I6', 'Tranche:', { bold: true, horizontal: 'center', border: { right: THIN } });
    w.set('J6', formula("'Disbursement note'!L6", note.trnch ?? DASH), {
      bold: true,
      horizontal: 'center',
      vertical: 'middle',
      border: true,
    });

    // ── Amounts ──
    w.set('A8:B8', 'Amount Recommended:', { bold: true, border: true });
    w.set('C8:E8', amountRecommended, { size: 12, horizontal: 'right', numFmt: ACCOUNTING_FMT, border: true });

    w.set('A9:B9', 'Amount in words:', { bold: true, border: true });
    w.set('C9:E9', data.amountInWordsText || DASH, { size: 12, horizontal: 'right', border: true });

    w.set('A10:B10', 'Exposure post disbursement:', {
      bold: true,
      border: { top: THIN, left: THIN, right: THIN },
    });
    w.set('C10:E10', formula("'Disbursement note'!F75", data.exposurePostDisbursement), {
      size: 12,
      horizontal: 'right',
      numFmt: ACCOUNTING_FMT,
      border: { top: THIN, left: THIN, right: THIN },
    });
    w.line(11, 'A', 'E', { top: THIN });

    // ── Comments ──
    w.set('A12:J12', data.comments || DASH, {
      italic: true,
      horizontal: 'left',
      vertical: 'middle',
      wrap: true,
      border: true,
    });

    // ── Signatures ──
    w.set('A13', 'Recommended by:', { bold: true });
    w.set('A20:C20', null, { horizontal: 'center' });
    w.set('I20:J20', null, { horizontal: 'center' });
    w.set('A21:C21', 'Head of Porfolio Management', { bold: true, horizontal: 'center', border: { top: THIN } });
    w.set('G21:I21', 'Chief Risk and Compliance Officer', {
      bold: true,
      size: 10.5,
      horizontal: 'center',
      border: { top: THIN },
    });

    w.set('A23', 'Approved by:', { bold: true });
    w.set('I30:J30', null, { bold: true, horizontal: 'center' });
    w.set('A31:C31', 'Chief Executive Officer', { bold: true, horizontal: 'center', border: { top: THIN } });

    // ── Outer medium frame ──
    w.line(31, 'A', 'J', { bottom: MEDIUM });
    w.sides(1, 31, 'A', 'J');
  }

  // ═══════════════════════ DISBURSEMENT NOTE (ANNEXURE I) ═══════════════════════

  /**
   * Replicates the template's "Disbursement note" sheet (columns B:L).
   *
   * Rows 1-78 are a fixed layout (header, monitoring block, exposure
   * breakdowns, Guarantee Calculations) and are addressed literally — the
   * Cover note and CAD sheets reference cells such as F45/F75 by address.
   * From the covenant table onward the row count is data-driven.
   *
   * Returns the addresses the CAD sheet's side panel needs to reference.
   */
  private buildDisbursementNoteSheet(
    workbook: ExcelJS.Workbook,
    data: DisbursementNoteWorkbookData,
  ): { equityCell: string | null; totalAssetsCell: string | null } {
    const { note } = data;
    const sheet = workbook.addWorksheet('Disbursement note', {
      properties: { defaultRowHeight: 14.4 },
      views: [{ showGridLines: false, zoomScale: 70 }],
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: { left: 0.04, right: 0.04, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
      },
    });
    const w = new SheetWriter(sheet, 12);

    sheet.columns = [
      {},
      { width: 9.77734375 },
      { width: 35.77734375 },
      { width: 13.6640625 },
      { width: 17.77734375 },
      { width: 16.33203125 },
      // G: first Latest Financials value column — wide enough for billions (was 14.44, showed ####).
      { width: 18 },
      { width: 24.5546875 },
      { width: 36.21875 },
      { width: 17 },
      { width: 17 },
      { width: 19 },
      { width: 9.5546875 },
      { width: 13.5546875 },
    ];

    const fixedHeights: Record<number, number> = { 1: 18, 2: 18.6, 3: 15, 4: 19.5, 5: 6, 6: 19.95, 7: 16.2 };

    for (let row = 8; row <= 76; row++) {
      fixedHeights[row] = 15.6;
    }

    [15, 30, 31, 45, 46, 60, 61, 75, 76].forEach((row) => (fixedHeights[row] = 16.2));
    fixedHeights[17] = 18;
    fixedHeights[28] = 18;
    fixedHeights[77] = 18;
    fixedHeights[78] = 4.95;
    w.heights(fixedHeights);

    const dateOrDash = (iso: string | null | undefined): string | Date => (iso ? new Date(iso) : DASH);
    const label = (range: string, text: string, border: Partial<ExcelJS.Borders> | boolean = true): void =>
      w.set(range, text, { bold: true, horizontal: 'left', vertical: 'middle', border });

    const approvedExposure = Number(note.aprvdExposure ?? 0);
    const currentOutstanding = Number(note.crntOutStndngExposure ?? 0);
    const amountRecommended = Number(data.amountRecommended ?? 0);
    const postDisbursement = currentOutstanding + amountRecommended;

    // ================= TITLE (rows 1-3) =================
    w.set('B1:L1', 'PAKISTAN MICROFINANCE INVESTEMENT COMPNAY LIMITED', {
      bold: true,
      size: 14,
      horizontal: 'center',
      vertical: 'middle',
      border: { top: MEDIUM, left: MEDIUM, right: MEDIUM },
    });
    w.set('B2:L2', 'DISBURSEMENT NOTE - Financing - Annexure I', {
      bold: true,
      size: 14,
      horizontal: 'center',
      vertical: 'middle',
      border: { bottom: MEDIUM, left: MEDIUM, right: MEDIUM },
    });
    w.line(3, 'B', 'L', { top: MEDIUM });

    // ================= BORROWER / FACILITY (rows 4-6, medium box) =================
    w.set('B4', 'Borrower Name:', { bold: true, vertical: 'middle' });
    w.border('C4', { right: THIN });
    w.set('D4', note.brwrNm || DASH, { vertical: 'middle' });
    w.border('I4', { right: THIN });
    w.line(4, 'B', 'I', { bottom: THIN });
    w.border('J4', { left: THIN, right: THIN });
    w.set('K4', 'Date:', { bold: true, horizontal: 'center', vertical: 'middle', border: { right: THIN, bottom: THIN } });
    w.set('L4', new Date(), {
      horizontal: 'center',
      vertical: 'middle',
      numFmt: DATE_FMT,
      border: { left: THIN, bottom: THIN },
    });

    label('B6:C6', 'Facility Name:', { right: THIN, top: THIN });
    w.set('D6', note.fcltyNm || DASH, { vertical: 'middle', border: { left: THIN, top: THIN } });
    w.line(6, 'D', 'F', { top: THIN });
    w.border('F6', { right: THIN });
    w.border('G6', { left: THIN, right: THIN });
    w.set('H6', 'Installment No: ', { bold: true, vertical: 'middle', border: { left: THIN, top: THIN } });
    w.set('I6', note.installmentNo ?? DASH, {
      horizontal: 'center',
      vertical: 'middle',
      numFmt: NUMBER_FMT,
      border: true,
    });
    w.border('J6', { left: THIN, right: THIN });
    w.set('K6', 'Tranche:', { bold: true, horizontal: 'center', vertical: 'middle', border: { right: THIN, top: THIN } });
    w.set('L6', note.trnch ?? DASH, {
      bold: true,
      horizontal: 'center',
      vertical: 'middle',
      border: { left: THIN, top: THIN },
    });
    w.outline('B4:L6', MEDIUM);

    // ================= APPROVAL / MONITORING (rows 8-15, medium box) =================
    // Monitoring fields, Addendum Date and Total Amount Disbursed come from `get-prpsd-dsbmnt`.
    const leftValue = (row: number, value: CellValue, numFmt?: string): void =>
      w.set(`D${row}:F${row}`, value, { horizontal: 'center', vertical: 'middle', numFmt, border: true });
    const rightLabel = (row: number, text: string): void =>
      w.set(`H${row}`, text, { bold: true, vertical: 'middle' });
    const rightValue = (row: number, value: CellValue, numFmt?: string): void =>
      w.set(`I${row}:L${row}`, value, { horizontal: 'center', vertical: 'middle', numFmt, border: true });

    label('B8:C8', 'Approval Date:');
    leftValue(8, dateOrDash(note.aprvlDt), DATE_FMT);
    rightLabel(8, 'PMD Last Field Visit Date:');
    rightValue(8, dateOrDash(note.pmdLstVstFldDt), DATE_FMT);

    label('B9:C9', 'Approving Authority:');
    leftValue(9, note.aprvngAthrty || DASH);
    rightLabel(9, 'Last instalment Due date:');
    rightValue(9, dateOrDash(note.lstInstDueDt), DATE_FMT);

    label('B10', 'Agreement Date:');
    w.border('C10', { top: THIN, left: THIN, bottom: THIN, right: THIN });
    leftValue(10, dateOrDash(note.agrmntDt), DATE_FMT);
    rightLabel(10, 'Date of last payment received:');
    rightValue(10, dateOrDash(note.lstPymntRcvdDt), DATE_FMT);

    label('B11', 'Addendum Date:');
    w.border('C11', { top: THIN, left: THIN, bottom: THIN, right: THIN });
    leftValue(11, dateOrDash(note.addendumDt), DATE_FMT);
    rightLabel(11, 'Previous IRS rating:');
    rightValue(11, note.prevIrsRtng || DASH);

    label('B12:C12', 'Agreement Period:');
    leftValue(12, note.agrmntPeriod || DASH);
    rightLabel(12, 'Latest IRS rating:');
    rightValue(12, note.ltstIrsRtng || DASH);

    label('B13', 'Disbursment Period:');
    w.border('C13', { top: THIN, left: THIN, bottom: THIN, right: THIN });
    leftValue(13, note.dsbmntPeriod || DASH);
    rightLabel(13, 'Up-front Fee:');
    // A number (never "-"): the Guarantee Calculations formulas multiply by it.
    // Sent as a whole percentage (e.g. 1.5) or a fraction — the cell is formatted as 0%.
    const upfrontFee = note.upfrntFee != null ? Number(note.upfrntFee) : null;
    rightValue(
      13,
      upfrontFee == null || Number.isNaN(upfrontFee) ? null : upfrontFee > 1 ? upfrontFee / 100 : upfrontFee,
      '0%',
    );

    label('B14:C14', 'Agreement Amount:');
    leftValue(14, Number(note.agrmntAmnt ?? 0), ACCOUNTING_FMT);
    rightLabel(14, 'USD @:');
    // USD rate from `get-crdt-grntrs`; the Guarantee Calculations formulas multiply by it.
    const usdRate = note.usdRt ?? data.creditGuarantors?.usdRt;
    rightValue(14, usdRate != null ? Number(usdRate) || null : null, '#,##0.00');

    label('B15:C15', 'Total Amount Disbursed:');
    leftValue(15, note.ttlAmntDsbsd != null ? Number(note.ttlAmntDsbsd) : DASH, ACCOUNTING_FMT);
    w.border('G15', { left: THIN });

    w.line(8, 'B', 'L', { top: MEDIUM });
    w.line(15, 'B', 'L', { bottom: MEDIUM });
    w.sides(8, 15, 'B', 'L');

    // ================= EXPOSURE BREAKDOWNS (rows 17-75) =================
    /**
     * One Conventional / Islamic block: a bold heading with its SUM on the
     * right, then one italic line per funding source. `values` maps a source
     * to a number or to a formula (for the post-disbursement block).
     */
    const breakdown = (
      headingRow: number,
      heading: string,
      values: CellValue[],
      sumResult: number,
      headingBorder: Partial<ExcelJS.Borders> = {},
      firstLabel?: string,
    ): void => {
      const firstLine = headingRow + 1;
      const sumEnd = headingRow + 5;

      w.set(`B${headingRow}:C${headingRow}`, heading, {
        bold: true,
        horizontal: 'left',
        vertical: 'middle',
        border: headingBorder,
      });
      w.set(`E${headingRow}`, formula(`SUM(E${firstLine}:E${sumEnd})`, sumResult), {
        bold: true,
        numFmt: NUMBER_FMT,
      });

      FUNDING_SOURCES.forEach((source, index) => {
        const row = firstLine + index;

        w.set(`B${row}`, index === 0 && firstLabel ? firstLabel : source, {
          italic: true,
          horizontal: 'left',
          vertical: 'middle',
        });
        w.set(`E${row}`, values[index] ?? 0, { size: 11, numFmt: NUMBER_FMT });
      });
    };

    /**
     * One repayment term's block (Current Outstanding, Amount Recommended or
     * Post-Disbursement): a heading value, then one line per guarantor of that
     * term (up to five, the rows the template leaves before the next block),
     * named and valued by `lineValue`.
     */
    const guarantorBreakdown = (
      headingRow: number,
      heading: string,
      rpymntTrm: number,
      headingValue: CellValue,
      lineValue: (grntr: CreditGuarantorDto, row: number) => CellValue,
      headingBorder: Partial<ExcelJS.Borders> = {},
    ): void => {
      w.set(`B${headingRow}:C${headingRow}`, heading, {
        bold: true,
        horizontal: 'left',
        vertical: 'middle',
        border: headingBorder,
      });
      w.set(`E${headingRow}`, headingValue, { bold: true, numFmt: NUMBER_FMT });

      termGuarantors(rpymntTrm).forEach((grntr, index) => {
        const row = headingRow + 1 + index;

        w.set(`B${row}`, grntr.grntrNm || DASH, { italic: true, horizontal: 'left', vertical: 'middle' });
        w.set(`E${row}`, lineValue(grntr, row), { size: 11, numFmt: NUMBER_FMT });
      });
    };

    /** A term's guarantors, in response order — every block lists them on the same rows. */
    const termGuarantors = (rpymntTrm: number): CreditGuarantorDto[] =>
      (data.creditGuarantors?.grntrs ?? [])
        .filter((grntr) => Number(grntr.rpymntTrm) === rpymntTrm)
        .slice(0, 5);

    /** Guarantee amount, always in PKR whatever the guarantor's currency. */
    const guaranteeAmount = (grntr: CreditGuarantorDto): number => Number(grntr.grntyAmntPkr ?? 0) || 0;

    /** This disbursement's saved share of a guarantor for a term (`get-prpsd-dsbmnt-grntr`). */
    const disbursementShare = (grntr: CreditGuarantorDto, rpymntTrm: number): number =>
      (data.guarantorShares ?? [])
        .filter(
          (share) =>
            Number(share.delFlg ?? 0) !== 1 &&
            Number(share.grntrSeq) === Number(grntr.grntrSeq) &&
            Number(share.rpymntTrm) === rpymntTrm,
        )
        .reduce((sum, share) => sum + (Number(share.grntrShare) || 0), 0);

    /** A "Total:" line with the double-underlined grand total in column F. */
    const totalLine = (row: number, expression: string, result: number): void => {
      w.set(`B${row}:C${row}`, 'Total:', { bold: true, horizontal: 'left', border: { top: THIN, right: THIN } });
      w.line(row, 'D', 'E', { top: THIN });
      w.set(`F${row}`, formula(expression, result), {
        bold: true,
        numFmt: ACCOUNTING_FMT,
        border: { top: THIN, bottom: DOUBLE },
      });
    };

    /** Boxed section title spanning B:F, e.g. "Approved Exposure". */
    const sectionTitle = (row: number, text: string, mergeLabel: boolean): void => {
      if (mergeLabel) {
        w.set(`B${row}:C${row}`, text, { bold: true, horizontal: 'left', vertical: 'middle', border: true });
        w.set(`D${row}:F${row}`, null, { bold: true, horizontal: 'center', numFmt: ACCOUNTING_FMT, border: true });
      } else {
        w.set(`B${row}`, text, { bold: true, vertical: 'middle', border: true });
        w.outline(`C${row}:F${row}`);
      }
    };

    // Approved Exposure — the DTO only carries the combined figure, shown on
    // the PMIC conventional line; other sources stay 0 until the API splits them.
    sectionTitle(17, 'Approved Exposure', true);
    w.line(18, 'B', 'F', { top: THIN });
    breakdown(18, 'Conventional', [approvedExposure, 0, 0, 0], approvedExposure, {}, 'PMIC (Including PRIME):');
    breakdown(24, 'Islamic', [0, 0, 0, 0], 0, {}, 'PMIC:');
    totalLine(30, 'E18+E24', approvedExposure);

    // Current Outstanding Exposure. Each type's heading shows the client's
    // outstanding for that facility type (`get-clnt-otstndng` `totalAmount`;
    // 17 Conventional, 18 Islamic) and the lines under it list that type's
    // guarantors with their guarantee amount in PKR.
    const outstandingTotal = (fcltyTypKey: number): number => {
      const entry = (data.clientOutstanding ?? []).find((item) => item.fcltyTypKey === fcltyTypKey);
      const response = entry?.response as { totalAmount?: unknown } | null | undefined;

      return Number(response?.totalAmount ?? 0) || 0;
    };
    const conventionalOutstanding = outstandingTotal(CONVENTIONAL_TERM);
    const islamicOutstanding = outstandingTotal(ISLAMIC_TERM);

    sectionTitle(32, 'Current Outstanding Exposure', false);
    w.line(33, 'D', 'F', { top: THIN });
    guarantorBreakdown(33, 'Conventional', CONVENTIONAL_TERM, conventionalOutstanding, guaranteeAmount, {
      top: THIN,
      right: THIN,
    });
    guarantorBreakdown(39, 'Islamic', ISLAMIC_TERM, islamicOutstanding, guaranteeAmount, { right: THIN });
    totalLine(45, 'E33+E39', conventionalOutstanding + islamicOutstanding);

    // Amount Recommended
    w.set('B47:C47', 'Amount Recommended:', { bold: true, horizontal: 'left', vertical: 'middle', border: true });
    w.outline('D47:F47');
    w.line(48, 'B', 'F', { top: THIN });
    // Guarantor lines carry this disbursement's guarantor share for that term.
    guarantorBreakdown(48, 'Conventional', CONVENTIONAL_TERM, amountRecommended, (grntr) =>
      disbursementShare(grntr, CONVENTIONAL_TERM),
    );
    guarantorBreakdown(54, 'Islamic', ISLAMIC_TERM, 0, (grntr) => disbursementShare(grntr, ISLAMIC_TERM));
    totalLine(60, 'E48+E54', amountRecommended);

    // Total Outstanding Exposure (Post-Disbursement) — same template idea:
    // each cell = Current Outstanding + Amount Recommended on the matching row
    // (heading with heading, each guarantor line with the same guarantor's line).
    sectionTitle(62, 'Total Outstanding Exposure (Post -Disbursement)', false);
    guarantorBreakdown(
      63,
      'Conventional',
      CONVENTIONAL_TERM,
      formula('E33+E48', conventionalOutstanding + amountRecommended),
      (_grntr, row) => formula(`E${row - 30}+E${row - 15}`),
      { top: THIN, right: THIN },
    );
    w.border('E63', { top: THIN });
    guarantorBreakdown(
      69,
      'Islamic',
      ISLAMIC_TERM,
      formula('E39+E54', islamicOutstanding),
      (_grntr, row) => formula(`E${row - 30}+E${row - 15}`),
      { right: THIN },
    );
    totalLine(75, 'E63+E69', conventionalOutstanding + islamicOutstanding + amountRecommended);

    // ================= DIRECT EXPOSURE (H65:I74) =================
    // Beside the post-disbursement block, as in the client's sheet. Total
    // Exposure is F75; every other figure comes from
    // GET /dsbmnt-note/get-prpsd-dsbmnt/{prpsdDsbmntSeq}.
    const sideLabel = (row: number, text: string): void =>
      w.set(`H${row}`, text, { horizontal: 'right', vertical: 'middle' });
    const sideValue = (row: number, value: unknown, numFmt = NUMBER_FMT): void =>
      w.set(`I${row}`, value == null || value === '' ? null : Number(value), { horizontal: 'right', numFmt });

    // Total Exposure is the Post-Disbursement block's grand total (F75).
    sideLabel(65, 'Total Exposure:');
    w.set('I65', formula('F75', conventionalOutstanding + islamicOutstanding + amountRecommended), {
      horizontal: 'right',
      numFmt: NUMBER_FMT,
    });

    sideLabel(66, 'Guarantees:');
    sideValue(66, note.gurantees);
    sideValue(67, note.guranteesPrcntage);

    sideLabel(69, 'Direct Exposure;');
    sideValue(69, note.directExpsr);

    sideLabel(71, 'PMIC Equity:');
    sideValue(71, note.pmicEquity);

    if (note.pmicEquityAsOnDt) {
      sideLabel(72, 'As on:');
      w.set('I72', new Date(note.pmicEquityAsOnDt), { horizontal: 'right', numFmt: DATE_FMT });
    }

    // pmicPercentage is already a percentage (0.08 = 0.08%), so it is divided for the % format.
    sideValue(74, note.pmicPercentage == null ? null : Number(note.pmicPercentage) / 100, '0.00%');

    // ================= GUARANTEE CALCULATIONS (I17:L35) =================
    // Guarantee figures come from `get-crdt-grntrs`.
    const gHeader = { bold: true, horizontal: 'center' as const, vertical: 'middle' as const, fill: GREY_FILL, border: true };
    const gLabel = { border: true };
    const gValue = (numFmt: string, horizontal: ExcelJS.Alignment['horizontal'] = 'center'): CellStyleOptions => ({
      horizontal,
      vertical: 'middle',
      numFmt,
      border: true,
    });

    // One column per guarantor of the proposal (J onward, at least the
    // template's three). With no guarantor data the template's DFC / BII /
    // NCGCL columns and formulas are kept as they are.
    const creditGuarantors = data.creditGuarantors?.grntrs ?? [];
    const useTemplateColumns = creditGuarantors.length === 0;
    const guarantorColumns = Array.from(
      { length: Math.max(3, creditGuarantors.length) },
      (_, index) => colLetter(colNumber('J') + index),
    );
    const lastGuarantorColumn = guarantorColumns[guarantorColumns.length - 1];
    const templateHeaders = ['DFC', 'BII', 'NCGCL'];

    guarantorColumns.slice(3).forEach((col) => {
      sheet.getColumn(col).width = 17;
    });

    w.set('I17', 'Guarantee Calculations', { bold: true, size: 14, horizontal: 'left', vertical: 'middle' });
    w.set('I18', 'Description', { ...gHeader, horizontal: undefined });
    guarantorColumns.forEach((col, index) => {
      const header = useTemplateColumns
        ? templateHeaders[index]
        : (creditGuarantors[index]?.grntrNm ?? null);

      w.set(`${col}18`, header, gHeader);
    });

    const guaranteeRows: Array<[number, string, CellValue[], string, ExcelJS.Alignment['horizontal']]> = [
      [19, 'Guaranty Amount (USD)', [null, null, null], GUARANTEE_FMT, 'center'],
      [20, 'Guaranty Amount (PKR)', [formula('J19*I14', 0), formula('K19*I14', 0), null], GUARANTEE_FMT, 'center'],
      [21, 'Risk Participation', [null, null, null], '0%', 'right'],
      [22, 'Guaranty Ceiling (USD)', [formula('J19*J21', 0), formula('K19*K21', 0), null], GUARANTEE_FMT, 'center'],
      [23, 'Guaranty Ceiling (PKR)', [formula('J20*J21', 0), formula('K20*K21', 0), formula('L20*L21', 0)], GUARANTEE_FMT, 'center'],
      [24, 'Maximum Approved Guarantee (USD)', [null, null, null], GUARANTEE_FMT, 'center'],
      [25, 'Maximum Approved Guarantee (PKR)', [formula('J24*I14', 0), formula('K24*I14', 0), null], GUARANTEE_FMT, 'center'],
      [26, 'Tenor (Years)', [null, null, null], GUARANTEE_FMT, 'center'],
    ];

    const num = (value: unknown): number => Number(value) || 0;
    // Risk participation arrives as a whole percentage (e.g. 50) — the cell is formatted as 0%.
    const ratio = (value: unknown): number => (num(value) > 1 ? num(value) / 100 : num(value));
    const guaranteeValues: Record<number, (grntr: CreditGuarantorDto) => number> = {
      19: (grntr) => num(grntr.grntyAmntUsd),
      20: (grntr) => num(grntr.grntyAmntPkr),
      21: (grntr) => ratio(grntr.rskPrtcptn),
      22: (grntr) => num(grntr.grntyCeilngUsd),
      23: (grntr) => num(grntr.grntyCeilngPkr),
      24: (grntr) => num(grntr.maxAprvdGrntyUsd),
      25: (grntr) => num(grntr.maxAprvdGrntyPkr),
      26: (grntr) => num(grntr.tnr),
    };

    guaranteeRows.forEach(([row, text, values, numFmt, horizontal]) => {
      w.set(`I${row}`, text, gLabel);
      guarantorColumns.forEach((col, index) => {
        const grntr = creditGuarantors[index];
        const value = useTemplateColumns
          ? values[index]
          : grntr
            ? guaranteeValues[row](grntr)
            : null;

        w.set(`${col}${row}`, value, gValue(numFmt, horizontal));
      });
    });

    w.set('I28', 'Limit Utilized', { bold: true, size: 14 });

    // Cumulative limits come from `get-grntr-lmt-utlzd`, matched to each
    // guarantor column by setup seq, else by name (rows of one guarantor summed).
    const columnKeys = guarantorColumns.map((_, index) =>
      useTemplateColumns
        ? { seq: null, name: templateHeaders[index] }
        : {
            seq: creditGuarantors[index]?.grntrSeq ?? null,
            name: creditGuarantors[index]?.grntrNm ?? '',
          },
    );
    const sameName = (a: string | null | undefined, b: string | null | undefined): boolean =>
      !!(a ?? '').trim() && (a ?? '').trim().toUpperCase() === (b ?? '').trim().toUpperCase();
    const cumulativeLimits = columnKeys.map((key) => {
      const rows = (data.guarantorLimits ?? []).filter((row) =>
        key.seq != null && row.stpGrntrSeq != null
          ? Number(row.stpGrntrSeq) === Number(key.seq)
          : sameName(row.grntrNm, key.name),
      );

      return rows.length
        ? {
            utilized: rows.reduce((sum, row) => sum + (Number(row.lmtUtlzdBrwr) || 0), 0),
            remaining: rows.reduce((sum, row) => sum + (Number(row.lmtRmngBrwr) || 0), 0),
          }
        : null;
    });

    // "By the Borrower" comes from the disbursement's saved guarantor shares
    // (`get-prpsd-dsbmnt-grntr`): utilized = grntrShare, remaining = lmtRmng.
    const borrowerLimits = columnKeys.map((key) => {
      const rows = (data.guarantorShares ?? []).filter(
        (row) =>
          Number(row.delFlg) !== 1 &&
          (key.seq != null && row.grntrSeq != null
            ? Number(row.grntrSeq) === Number(key.seq)
            : sameName(row.grntrNm, key.name)),
      );

      return rows.length
        ? {
            utilized: rows.reduce((sum, row) => sum + (Number(row.grntrShare) || 0), 0),
            remaining: rows.reduce((sum, row) => sum + (Number(row.lmtRmng) || 0), 0),
          }
        : null;
    });

    const limitBlock = (
      headingRow: number,
      heading: string,
      ceilingRow: number,
      limits: Array<{ utilized: number; remaining: number } | null> = [],
    ): void => {
      w.set(`I${headingRow}`, heading, { bold: true, horizontal: 'left', vertical: 'middle', fill: GREY_FILL });
      guarantorColumns.forEach((col, index) => {
        w.set(
          `${col}${headingRow}`,
          null,
          index === 0
            ? { bold: true, horizontal: 'right', vertical: 'middle', fill: GREY_FILL }
            : { fill: GREY_FILL },
        );
      });
      w.outline(`I${headingRow}:${lastGuarantorColumn}${headingRow}`);
      w.border(`J${headingRow}`, { left: THIN });

      w.set(`I${headingRow + 1}`, 'Limit Utilized:', { horizontal: 'left', border: true });
      w.set(`I${headingRow + 2}`, 'Limit Remaining:', { horizontal: 'left', border: true });

      guarantorColumns.forEach((col, index) => {
        const limit = limits[index];
        // A spare column (fewer guarantors than the template's three) stays blank.
        const spare = !useTemplateColumns && !creditGuarantors[index];

        w.set(`${col}${headingRow + 1}`, limit ? limit.utilized : null, gValue(NUMBER_FMT, 'right'));
        w.set(
          `${col}${headingRow + 2}`,
          limit
            ? limit.remaining
            : spare
              ? null
              : formula(`${col}${ceilingRow}-${col}${headingRow + 1}`, 0),
          gValue(NUMBER_FMT, 'right'),
        );
      });
    };

    limitBlock(29, 'By the Borrower', 25, borrowerLimits);
    limitBlock(33, 'Cumulative:', 23, cumulativeLimits);

    // ================= COVENANTS (row 77 onward) =================
    w.set('B77', 'Covenants', { bold: true, underline: true, size: 14, horizontal: 'left' });

    let row = 79;
    const covenantTop = row;

    sheet.getRow(row).height = 18.45;
    w.set(`B${row}`, 'S. No.', { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    w.set(`C${row}:F${row}`, 'Description/ Covenants', {
      bold: true,
      horizontal: 'center',
      vertical: 'middle',
      border: true,
    });
    w.set(`G${row}:H${row}`, 'Compliance Status (Yes/No/Not Due)', {
      bold: true,
      horizontal: 'center',
      vertical: 'middle',
      wrap: true,
      border: true,
    });
    w.set(`I${row}:L${row}`, 'Remarks ', { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    row++;

    // Keep at least one (blank) line so the table still prints as a box.
    const covenants: CovenantExportRow[] = data.covenantRows.length
      ? data.covenantRows
      : [{ srNo: 1, description: '', complianceStatus: '', remarks: '' }];

    for (const covenant of covenants) {
      w.set(`B${row}`, covenant.srNo, { horizontal: 'center', vertical: 'middle', border: true });
      w.set(`C${row}:F${row}`, covenant.description, {
        horizontal: 'left',
        vertical: 'middle',
        wrap: true,
        border: true,
      });
      w.set(`G${row}:H${row}`, covenant.complianceStatus, { horizontal: 'center', vertical: 'middle', border: true });
      w.set(`I${row}:L${row}`, covenant.remarks, { horizontal: 'left', vertical: 'middle', wrap: true, border: true });
      sheet.getRow(row).height = wrapHeight([covenant.description, covenant.remarks], 82);
      row++;
    }

    w.outline(`B${covenantTop}:L${row - 1}`, MEDIUM);

    // ================= LATEST FINANCIALS =================
    sheet.getRow(row).height = 15.6; // blank gap row
    row++;

    sheet.getRow(row).height = 15.6;
    w.set(`B${row}:E${row}`, 'Latest Financials', { bold: true, underline: true, horizontal: 'left' });
    row++;

    sheet.getRow(row).height = 4.95;
    row++;

    const financialTop = row;
    const periods = data.financialPeriods.slice(0, FINANCIAL_PERIOD_COLUMNS.length);

    sheet.getRow(row).height = 43.95;
    w.set(`B${row}`, 'S No', { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    w.set(`C${row}:F${row}`, 'Indicator', { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    FINANCIAL_PERIOD_COLUMNS.forEach((col, index) => {
      w.set(`${col}${row}`, periods[index] ?? '', {
        bold: true,
        horizontal: 'center',
        vertical: 'middle',
        wrap: true,
        border: true,
      });
    });
    row++;

    let equityCell: string | null = null;
    let totalAssetsCell: string | null = null;
    // CAD reads equity / total assets from the "Latest Management Accounts"
    // column (H) like the template; fall back to the first period if absent.
    const referenceColumn = FINANCIAL_PERIOD_COLUMNS[periods.length > 1 ? 1 : 0];

    const financialRows: FinancialExportRow[] = data.financialRows.length
      ? data.financialRows
      : [{ indicator: '', values: [] }];

    financialRows.forEach((financialRow, index) => {
      sheet.getRow(row).height = 15.6;
      w.set(`B${row}`, index + 1, { horizontal: 'center', border: true });
      w.set(`C${row}:F${row}`, financialRow.indicator, { horizontal: 'left', border: true });

      FINANCIAL_PERIOD_COLUMNS.forEach((col, periodIndex) => {
        const cell =
          periodIndex < periods.length ? financialCell(financialRow.values[periodIndex]) : { value: null };

        w.set(`${col}${row}`, cell.value, {
          horizontal: typeof cell.value === 'number' ? undefined : 'center',
          vertical: 'middle',
          numFmt: cell.numFmt,
          border: true,
        });
      });

      const indicator = (financialRow.indicator || '').trim().toLowerCase();

      if (!equityCell && /^equity\b/.test(indicator)) {
        equityCell = `${referenceColumn}${row}`;
      }

      if (!totalAssetsCell && /^total assets\b/.test(indicator)) {
        totalAssetsCell = `${referenceColumn}${row}`;
      }

      row++;
    });

    // Footnote box beneath the table (4 rows, like the template's B108:I111).
    const footnoteTop = row;

    for (let footRow = footnoteTop; footRow < footnoteTop + 4; footRow++) {
      sheet.getRow(footRow).height = footRow === footnoteTop + 3 ? 16.2 : 15.6;
    }

    w.set(`C${footnoteTop}`, null, { italic: true, horizontal: 'left' });
    row = footnoteTop + 4;

    w.outline(`B${financialTop}:I${row - 1}`, MEDIUM);
    w.line(footnoteTop, 'B', 'I', { top: THIN });

    // ================= COMMENTS =================
    sheet.getRow(row).height = 15.6; // gap
    row++;

    sheet.getRow(row).height = 16.05;
    w.set(`B${row}:L${row}`, 'Comments (including any key highlights from field visit):\n', {
      bold: true,
      underline: true,
      horizontal: 'left',
      vertical: 'top',
      wrap: true,
    });
    row++;

    sheet.getRow(row).height = 5.55;
    row++;

    const commentTop = row;

    w.set(`B${row}:L${row + 2}`, data.comments || DASH, {
      horizontal: 'left',
      vertical: 'top',
      wrap: true,
      border: { top: MEDIUM, left: MEDIUM, bottom: MEDIUM, right: MEDIUM },
    });
    sheet.getRow(row).height = 27.45;
    sheet.getRow(row + 1).height = 34.5;
    sheet.getRow(row + 2).height = 43.05;
    row = commentTop + 3;

    // ================= SIGNATURES (medium box) =================
    const signatureTop = row;

    sheet.getRow(row).height = 14.55;
    row++;

    w.set(`B${row}`, 'Recommended By:', { bold: true });

    for (let sigRow = row; sigRow <= row + 6; sigRow++) {
      sheet.getRow(sigRow).height = 15.6;
    }

    row += 5;

    w.set(`B${row}:D${row}`, null, { horizontal: 'center', border: { bottom: THIN } });
    w.set(`J${row}:L${row}`, null, { horizontal: 'center', border: { bottom: THIN } });
    row++;

    w.set(`B${row}:D${row}`, 'Relationship Manager', { bold: true, horizontal: 'center', border: { top: THIN } });
    w.set(`J${row}:L${row}`, 'Head of Portfolio Management', {
      bold: true,
      horizontal: 'center',
      border: { top: THIN },
    });
    row++;

    sheet.getRow(row).height = 16.2;
    w.outline(`B${signatureTop}:L${row}`, MEDIUM);

    sheet.pageSetup.printArea = `B1:L${row}`;

    return { equityCell, totalAssetsCell };
  }

  // ═══════════════════════════════ CAD (ANNEXURE II) ═══════════════════════════════

  /**
   * Replicates the template's "CAD" sheet: the Credit Administration
   * checklist (A:J) plus the exposure-limit side panel (L:M), whose
   * formulas read Annexure I by address exactly like the template.
   */
  private buildCADSheet(
    workbook: ExcelJS.Workbook,
    data: DisbursementNoteWorkbookData,
    annexure: { equityCell: string | null; totalAssetsCell: string | null },
  ): void {
    const { note } = data;

    // The checklist is the CAD tab's questionnaire, so its length varies;
    // everything below it (footnote, comment box, signatures) shifts with it.
    // The template's 17-item layout lands on the same rows (25 / 26 / 42).
    const cadRows = data.cadRows;
    const firstItemRow = 9;
    const lastItemRow = firstItemRow + Math.max(cadRows.length, 1) - 1;
    const footnoteRow = lastItemRow + 1;
    const commentStartRow = footnoteRow + 1;
    const commentEndRow = footnoteRow + 6;
    const recommendedRow = footnoteRow + 7;
    const signatureRow = footnoteRow + 15;
    const lastRow = footnoteRow + 16;

    const sheet = workbook.addWorksheet('CAD', {
      properties: { defaultRowHeight: 18 },
      views: [{ showGridLines: false, zoomScale: 60 }],
      pageSetup: {
        orientation: 'portrait',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        printArea: `A1:J${lastRow}`,
        margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
      },
    });
    const w = new SheetWriter(sheet, 14);

    sheet.columns = [
      { width: 8.77734375 },
      { width: 26.5546875 },
      { width: 56 },
      { width: 22.33203125 },
      { width: 15.5546875 },
      { width: 13.5546875 },
      { width: 7.5546875 },
      { width: 17.21875 },
      { width: 12.77734375 },
      { width: 25.21875 },
      { width: 1.77734375 },
      { width: 49.109375 },
      { width: 18.5546875 },
      { width: 13.6640625 },
    ];

    w.heights({
      2: 18.6, 4: 27.45, 5: 6, 6: 32.55, 8: 28.05,
      [footnoteRow]: 25.05,
      [commentStartRow]: 22.5,
      [recommendedRow + 1]: 38.55,
      [recommendedRow + 3]: 36,
      [lastRow]: 18.6,
    });

    const amountRecommended = Number(data.amountRecommended ?? 0);
    const currentOutstanding = Number(note.crntOutStndngExposure ?? 0);
    const million = (value: number): number => value / 1_000_000;

    // ================= TITLE (rows 1-3) =================
    w.set('A1:J1', 'PAKISTAN MICROFINANCE INVESTEMENT COMPNAY LIMITED', {
      bold: true,
      horizontal: 'center',
      border: { top: MEDIUM },
    });
    w.set('A2:J2', 'DISBURSEMENT NOTE - Financing - Annexure II', {
      bold: true,
      horizontal: 'center',
      border: { bottom: MEDIUM },
    });
    w.line(3, 'A', 'J', { top: MEDIUM });

    // ================= BORROWER / FACILITY (rows 4-6) =================
    w.set('A4', 'Borrower Name', { bold: true, horizontal: 'left', vertical: 'middle' });
    w.set('C4', formula("'Disbursement note'!D4", note.brwrNm || DASH), { bold: true, vertical: 'middle' });
    w.outline('C4:H4');
    w.set('I4', 'Date:', { bold: true, horizontal: 'center' });
    w.set('J4', new Date(), { horizontal: 'center', vertical: 'middle', numFmt: DATE_FMT, border: true });

    w.set('A6:B6', 'Facility Name:', { bold: true, horizontal: 'left', vertical: 'middle' });
    w.set('C6', formula("'Disbursement note'!D6", note.fcltyNm || DASH), { bold: true, vertical: 'middle' });
    w.outline('C6:E6');
    w.set('F6:G6', 'Installment No: ', {
      bold: true,
      horizontal: 'center',
      vertical: 'middle',
      border: { left: THIN, right: THIN },
    });
    w.set('H6', note.installmentNo ?? DASH, { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    w.set('I6', 'Tranche:', { bold: true, horizontal: 'center', vertical: 'middle', border: { right: THIN } });
    w.set('J6', note.trnch ?? DASH, { bold: true, horizontal: 'center', vertical: 'middle', border: true });

    // ================= CHECKLIST (row 8 header, then one row per CAD question) =================
    w.set('A8', 'S No', { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    w.set('B8:C8', 'Description', { bold: true, horizontal: 'center', vertical: 'middle', border: true });
    w.set('D8', 'Status', { bold: true, horizontal: 'center', vertical: 'middle', wrap: true, border: true });
    w.set('E8', 'Remarks', { bold: true, horizontal: 'center', vertical: 'middle' });
    w.outline('E8:J8');

    if (!cadRows.length) {
      w.set(`A${firstItemRow}:J${firstItemRow}`, 'No CAD questions configured.', {
        horizontal: 'center',
        vertical: 'middle',
        border: true,
      });
      sheet.getRow(firstItemRow).height = 30;
    }

    // Ratio questions whose Status comes from `get-prpsd-dsbmnt`, not the CAD answer.
    // Matched by question text (S No as fallback, since the list is configurable).
    const amount = (value: unknown, suffix: string): string | null =>
      value == null || value === '' || Number.isNaN(Number(value))
        ? null
        : `${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}${suffix}`;
    const computedStatuses: Array<{ srNo: number; matches: RegExp; value: string | null }> = [
      { srNo: 14, matches: /5 times of borrowers? equity/i, value: amount(note.pmicExpsrEqtyX, '') },
      { srNo: 16, matches: /percentage of PMIC Equity/i, value: amount(note.pstDsbmntExpsrPct, '%') },
      { srNo: 17, matches: /PMIC Exp\w* as %age of Total Assets/i, value: amount(note.pmicExpsrAsstPct, '%') },
    ];
    const computedStatus = (item: CovenantExportRow): string | null => {
      const byText = computedStatuses.find((entry) => entry.matches.test(item.description));
      const entry = byText ?? computedStatuses.find((candidate) => candidate.srNo === Number(item.srNo));

      return entry?.value ?? null;
    };

    cadRows.forEach((item, index) => {
      const row = firstItemRow + index;
      const remarks = item.remarks && item.remarks !== DASH ? item.remarks : null;

      w.set(`A${row}`, item.srNo, { horizontal: 'center', vertical: 'middle', wrap: true, border: true });
      w.set(`B${row}:C${row}`, item.description, { horizontal: 'left', vertical: 'middle', wrap: true, border: true });
      w.set(`D${row}`, computedStatus(item) ?? (item.complianceStatus || DASH), {
        horizontal: 'center',
        vertical: 'middle',
        wrap: true,
        border: true,
      });
      w.set(`E${row}:J${row}`, remarks, { horizontal: 'left', vertical: 'middle', wrap: true, border: true });

      // B:C fits ~70 characters a line at this width; grow for longer questions.
      const lines = Math.max(
        Math.ceil(item.description.length / 70),
        Math.ceil((remarks ?? '').length / 90),
        1,
      );
      sheet.getRow(row).height = Math.max(30, lines * 15);
    });

    // ================= FOOTNOTE + CAD COMMENT BOX =================
    w.set(`A${footnoteRow}`, '*This pertains to Sub-ordinated loan', { horizontal: 'left', vertical: 'middle' });
    w.set(
      `E${footnoteRow}:J${footnoteRow}`,
      'For D/E between 2-3x, borrowing from single source can be upto 70% of total assets.',
      { horizontal: 'left', vertical: 'middle', wrap: true },
    );
    w.set(`A${commentStartRow}:J${commentEndRow}`, null, {
      size: 14,
      horizontal: 'left',
      vertical: 'top',
      wrap: true,
      border: true,
    });

    // ================= SIGNATURES =================
    w.set(`A${recommendedRow}`, 'Recommended by:', { bold: true });
    w.set(`A${signatureRow}:C${signatureRow}`, '(Manager CAD)', {
      bold: true,
      horizontal: 'center',
      border: { top: THIN },
    });
    w.set(`I${signatureRow}:J${signatureRow}`, 'Chief Risk and Compliance Officer', {
      bold: true,
      horizontal: 'center',
      border: { top: THIN },
    });

    // ── Outer medium frame. The template keeps a thin right edge on the
    // header row and around the comment box — kept for fidelity. ──
    w.line(lastRow, 'A', 'J', { bottom: MEDIUM });

    for (let row = 1; row <= lastRow; row++) {
      w.border(`A${row}`, { left: MEDIUM });

      if (row !== 8 && (row < commentStartRow || row > commentEndRow)) {
        w.border(`J${row}`, { right: MEDIUM });
      }
    }

    // ================= SIDE PANEL (L9:M40) =================
    const panelLabel = (address: string, text: string, options: CellStyleOptions = {}): void =>
      w.set(address, text, { horizontal: 'left', vertical: 'middle', ...options });
    const panelValue = (address: string, value: CellValue, options: CellStyleOptions = {}): void =>
      w.set(address, value, { horizontal: 'center', vertical: 'middle', numFmt: NUMBER_FMT, ...options });

    const postDisbursementM = million(currentOutstanding + amountRecommended);

    panelLabel('L9', 'Borrower', { bold: true, border: { bottom: THIN } });
    w.border('M9', { bottom: THIN });
    panelLabel('L10', 'Post-Disb. Exposure:');
    panelValue('M10', formula("'Disbursement note'!F75/1000000", postDisbursementM));
    panelLabel('L11', 'Equity:');
    panelValue('M11', annexure.equityCell ? formula(`'Disbursement note'!${annexure.equityCell}`) : null);
    panelLabel('L12', 'Total Assets:');
    panelValue('M12', annexure.totalAssetsCell ? formula(`'Disbursement note'!${annexure.totalAssetsCell}`) : null);

    // PMIC's own balance-sheet figures (in millions) — no data source yet.
    panelLabel('L13', 'PMIC', { bold: true, border: { bottom: THIN } });
    w.border('M13', { bottom: THIN });
    panelLabel('L14', 'Gross Loan ');
    panelValue('M14', null);
    panelLabel('L15', 'PMIC Equity');
    panelValue('M15', null);
    panelLabel('L16', 'Sub Debt');
    panelValue('M16', null);
    panelLabel('L17', 'Total Equity ');
    panelValue('M17', formula('M15+M16', 0));

    panelLabel('L18', 'Exposure - Net of Guarantee', { bold: true, horizontal: undefined, border: { bottom: THIN } });
    w.border('M18', { bottom: THIN });
    panelLabel('L19', 'Sub-Debt Repayment:', { horizontal: undefined });
    panelValue('M19', null);
    panelLabel('L20', 'Total Equity after Sub-Debt Repayment:');
    panelValue('M20', formula('M17-M19', 0));

    panelLabel('L22', 'Amount tagged to DFC:');
    panelValue('M22', formula("('Disbursement note'!E35+'Disbursement note'!E41)/1000000", 0));
    panelLabel('L23', 'Amount tagged to NCGCL:');
    panelValue('M23', formula("('Disbursement note'!E36+'Disbursement note'!E42)/1000000", 0));
    panelLabel('L24', 'BII');
    panelValue('M24', formula("('Disbursement note'!E37+'Disbursement note'!E43)/1000000", 0));
    panelLabel('L26', 'Total Guaranted Amount:');
    panelValue('M26', formula('SUM(M22:M25)', 0));
    panelLabel('L27', '85% allowed as per SECP:');
    panelValue('M27', formula('M26*0.85', 0));

    panelLabel('L29', 'Current Outs. Exposure (Total):', { horizontal: undefined, vertical: undefined, border: true });
    panelValue('M29', formula("'Disbursement note'!F45/1000000", million(currentOutstanding)), { border: true });
    panelLabel('L30', 'Current Outs. Exposure (Net of Guarantee):', { border: true });
    panelValue('M30', formula('M29-M27', million(currentOutstanding)), { border: true });
    panelLabel('L31', 'Max Exposure Allowed (25% of Equity):', { border: true });
    panelValue('M31', formula('M20*25%', 0), { border: true });
    panelLabel('L32', 'Max Disbursement Allowed:', { fill: GREY_FILL, border: { top: THIN, bottom: THIN, left: THIN } });
    panelValue('M32', formula('M31-M30'), { fill: GREY_FILL, border: { top: THIN, bottom: THIN, right: THIN } });

    panelLabel('L35', 'Current Outs. Exposure (Total):', { horizontal: undefined, vertical: undefined, border: true });
    panelValue('M35', formula('M29', million(currentOutstanding)), { border: true });
    panelLabel('L36', 'Max Exposure Allowed (25% of Equity+Guarantee):', { wrap: true, border: true });
    panelValue('M36', formula('M31+M27', 0), { border: true });
    panelLabel('L37', 'Max Disbursement Allowed:', { fill: GREY_FILL, border: { top: THIN, bottom: THIN, left: THIN } });
    panelValue('M37', formula('M36-M35'), { fill: GREY_FILL, border: { top: THIN, bottom: THIN, right: THIN } });

    panelLabel('L39', 'Disbursement Proposed:', { horizontal: undefined, vertical: undefined });
    panelValue(
      'M39',
      formula("('Disbursement note'!E49+'Disbursement note'!E55)/1000000", million(amountRecommended)),
      { numFmt: undefined },
    );
    panelLabel('L40', 'Post-Disb Exposure (Net of Guarantee):', {
      bold: true,
      horizontal: undefined,
      vertical: undefined,
      fill: DARK_FILL,
      border: { top: THIN, bottom: THIN, left: THIN },
    });
    panelValue('M40', formula('M30+M39', postDisbursementM), { bold: true, fill: DARK_FILL, border: true });
  }
}
